(() => {
  const STORAGE_KEY = 'project-q:wishlist:v1';
  const listeners = new Set();

  const normalize = (item) => ({
    id: String(item?.id || ''),
    title: String(item?.title || ''),
    url: String(item?.url || ''),
    image: String(item?.image || ''),
    price: String(item?.price || ''),
  });

  const readStoredItems = () => {
    try {
      if (typeof localStorage === 'undefined') return [];
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.map(normalize).filter((item) => item.id && item.title && item.url);
    } catch (_error) {
      return [];
    }
  };

  let savedItems = readStoredItems();

  const persist = () => {
    try {
      if (typeof localStorage === 'undefined') return false;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(savedItems));
      return true;
    } catch (_error) {
      return false;
    }
  };

  const emit = (detail) => {
    listeners.forEach((listener) => listener(items(), detail));
  };

  const items = () => savedItems.map((item) => ({ ...item, ...(window.ProjectQProductLookup?.[item.id] || {}) }));
  const has = (id) => savedItems.some((item) => item.id === String(id));

  const remove = (id) => {
    const normalizedId = String(id);
    const before = savedItems.length;
    savedItems = savedItems.filter((item) => item.id !== normalizedId);
    if (savedItems.length === before) return { removed: false, stored: true };
    const stored = persist();
    emit({ action: 'remove', id: normalizedId, stored });
    return { removed: true, stored };
  };

  const toggle = (item) => {
    const normalized = normalize(item);
    if (!normalized.id || !normalized.title || !normalized.url) {
      return { added: false, stored: false };
    }
    if (has(normalized.id)) {
      const result = remove(normalized.id);
      return { added: false, stored: result.stored };
    }
    savedItems = [...savedItems, normalized];
    const stored = persist();
    emit({ action: 'add', id: normalized.id, stored });
    return { added: true, stored };
  };

  const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  window.ProjectQWishlist = Object.freeze({ items, has, toggle, remove, subscribe });

  const initWishlistUi = () => {
    const drawer = document.querySelector('[data-pq-wishlist-drawer]');
    const list = document.querySelector('[data-pq-wishlist-list]');
    const empty = document.querySelector('[data-pq-wishlist-empty]');
    const status = document.querySelector('[data-pq-wishlist-status]');
    const openButton = document.querySelector('[data-pq-wishlist-open]');
    const backdrop = document.querySelector('.pq-wishlist-backdrop');
    if (!drawer || !list || !empty || !openButton || !backdrop) return;

    const updateToggles = () => {
      document.querySelectorAll('[data-pq-wishlist-toggle]').forEach((button) => {
        const active = has(button.dataset.productId);
        button.setAttribute('aria-pressed', String(active));
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-label', `${active ? window.ProjectQI18n.remove_wishlist : window.ProjectQI18n.add_wishlist}：${button.dataset.productTitle}`);
      });
    };

    const updateCount = () => {
      const count = savedItems.length;
      document.querySelectorAll('[data-pq-wishlist-count]').forEach((element) => {
        element.textContent = String(count);
        element.hidden = count === 0;
      });
    };

    const createItem = (item) => {
      const row = document.createElement('article');
      row.className = 'pq-wishlist-item';

      if (item.image) {
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = '';
        image.width = 96;
        image.height = 96;
        image.loading = 'lazy';
        row.append(image);
      }

      const copy = document.createElement('div');
      const link = document.createElement('a');
      link.href = window.ProjectQLocalizedUrl(item.url);
      link.textContent = item.title;
      copy.append(link);

      if (item.price) {
        const price = document.createElement('span');
        price.textContent = item.price;
        copy.append(price);
      }

      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.dataset.pqWishlistRemove = item.id;
      removeButton.textContent = window.ProjectQI18n.remove;
      removeButton.setAttribute('aria-label', `${window.ProjectQI18n.remove_wishlist}: ${item.title}`);
      copy.append(removeButton);
      row.append(copy);
      return row;
    };

    const render = () => {
      list.replaceChildren(...items().map(createItem));
      empty.hidden = savedItems.length > 0;
      updateToggles();
      updateCount();
    };

    const openDrawer = () => {
      render();
      drawer.hidden = false;
      backdrop.hidden = false;
      document.body.classList.add('pq-wishlist-is-open');
      drawer.querySelector('[data-pq-wishlist-close]')?.focus();
    };

    const closeDrawer = () => {
      drawer.hidden = true;
      backdrop.hidden = true;
      document.body.classList.remove('pq-wishlist-is-open');
      openButton.focus();
    };

    document.addEventListener('click', (event) => {
      const toggleButton = event.target.closest('[data-pq-wishlist-toggle]');
      if (toggleButton) {
        event.preventDefault();
        const result = toggle({
          id: toggleButton.dataset.productId,
          title: toggleButton.dataset.productTitle,
          url: toggleButton.dataset.productUrl,
          image: toggleButton.dataset.productImage,
          price: toggleButton.dataset.productPrice,
        });
        if (status) {
          status.textContent = result.stored
            ? (result.added ? window.ProjectQI18n.wishlist_added : window.ProjectQI18n.wishlist_removed).replace('__TITLE__', toggleButton.dataset.productTitle)
            : window.ProjectQI18n.wishlist_storage_error;
        }
        render();
        return;
      }

      const removeButton = event.target.closest('[data-pq-wishlist-remove]');
      if (removeButton) {
        remove(removeButton.dataset.pqWishlistRemove);
        render();
        return;
      }

      if (event.target.closest('[data-pq-wishlist-open]')) openDrawer();
      if (event.target.closest('[data-pq-wishlist-close]')) closeDrawer();
    });

    document.addEventListener('keydown', (event) => {
      if (drawer.hidden) return;
      if (event.key === 'Escape') {
        closeDrawer();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]')]
        .filter(element => element.tabIndex >= 0 && element.getClientRects().length > 0);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    subscribe(render);
    render();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWishlistUi);
  } else {
    initWishlistUi();
  }
})();
